// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { ReactNode, useEffect, useState } from "react";

import { mdiAlert, mdiDelete, mdiPlus } from "@mdi/js";
import { Button } from "antd";
import _debounce from "lodash/debounce";
import styled from "styled-components";
import formatPropertyName from "utils/column/formatPropertyName";
import getColumnLabelWithContext, {
  getFlatDisplayTitle
} from "utils/column/getColumnLabelWithContext";
import { toPdenEnum } from "utils/toPdenEnum";

import { useColumns, useGlobalProductionSettings } from "hooks";
import { useFacilityColumns } from "hooks/useColumns";

import { EntityKind } from "models/entityKind";

import { Tooltip } from "components/base";
import { GeomBinProvider } from "components/geom-bin/contexts/GeomBinContext";
import GroupBy from "components/groupBy";
import { parseTileQualifications, qualifyTileProperty } from "components/groupBy/helpers";
import {
  shapefileColumnFromKey,
  useIsDeletedShapefileField,
  useIsShapefileFieldAbsentFromProject,
  useResolveShapefileColumn
} from "components/project/shapefiles/hooks/useResolveShapefileColumn";
import { useResolvedDefaultFieldList } from "components/user-settings/hooks";

import {
  ErrorContainer,
  SectionActionsContainer,
  SectionHeading,
  SectionHeadingTitleContainer,
  SectionInputWrapper,
  StyledSpinner,
  WarningContainer
} from "./shared";

interface FocusTilePickerProps {
  entityKind: EntityKind;
  tiles: string[];
  onChange: (tiles: string[]) => void;
  isSaving: boolean;
  excludedCategoryFilters: string[];
  errorMessage?: Maybe<string>;
  title?: string;
  headerActions?: ReactNode;
  // The org endpoint rejects more than 12 raw entries, so a stale tile must hold its slot there.
  capRawTiles?: boolean;
}

const FocusTilePicker = ({
  entityKind,
  tiles,
  onChange,
  isSaving,
  excludedCategoryFilters,
  errorMessage,
  title,
  headerActions,
  capRawTiles = false
}: FocusTilePickerProps) => {
  const columns = useColumns();
  const facilityColumns = useFacilityColumns();
  const resolveShapefileColumn = useResolveShapefileColumn();
  const isDeletedShapefileField = useIsDeletedShapefileField();
  const isAbsentShapefileField = useIsShapefileFieldAbsentFromProject();
  const globalSettings = useGlobalProductionSettings(EntityKind.Well);
  const columnsToUse = entityKind === EntityKind.Well ? columns : facilityColumns;
  const isLoading = columnsToUse.length === 0;

  const [duplicateError, setDuplicateError] = useState("");
  const [warning, setWarning] = useState("");

  // Tiles that don't currently resolve to a live column - either the field
  // was deleted, or it's just out of context (e.g. a project-scoped field
  // with no project selected). Excluded from the max-count/warning logic
  // below so a deleted field can't permanently occupy a slot, but never
  // auto-persisted - see useResolvedDefaultFieldList for why.
  const resolvedTiles = useResolvedDefaultFieldList({
    rawList: tiles,
    columnsToUse,
    isLoading,
    resolveFallbackColumn: resolveShapefileColumn,
    isAbsentField: isAbsentShapefileField
  });

  useEffect(() => {
    setDuplicateError("");
  }, [tiles]);

  useEffect(() => {
    if (resolvedTiles.length < 6 || resolvedTiles.length > 10) {
      setWarning("EVA is optimized for 6-10 focus tiles");
    } else {
      setWarning("");
    }
  }, [resolvedTiles]);

  const handleTileChange = (index: number, propertyName: string) => {
    if (tiles.includes(propertyName)) {
      setDuplicateError("Tile already set as a default");
      return;
    }
    const newTiles = [...tiles];
    newTiles[index] = propertyName;
    onChange(newTiles);
  };

  const debouncedAddFocusTile = _debounce((newTiles: string[]) => {
    onChange(newTiles);
  }, 400);

  const handleAddFocusTile = () => {
    const next = columnsToUse.filter((c) => !tiles.includes(c.property))[0];
    debouncedAddFocusTile([...tiles, next.property]);
  };

  const handleRemoveFocusTile = (e: MouseEvent, index: number) => {
    const newTiles = [...tiles];
    newTiles.splice(index, 1);
    onChange(newTiles);

    e.stopPropagation();
  };

  const error = errorMessage || duplicateError;

  return (
    <GeomBinProvider>
      <div>
        <SectionHeading>
          <SectionHeadingTitleContainer>
            {title ?? `${entityKind} Focus Tiles`}
            {warning && (
              <WarningContainer>
                <Icon path={mdiAlert} size={1} /> {warning}
              </WarningContainer>
            )}
          </SectionHeadingTitleContainer>

          <SectionActionsContainer>
            {(capRawTiles ? tiles : resolvedTiles).length < 12 && (
              <Button
                data-testid={`${entityKind}-default-focus-tiles-add`}
                type="text"
                icon={<Icon path={mdiPlus} size={1} />}
                onClick={handleAddFocusTile}
                loading={isSaving}>
                Add
              </Button>
            )}
            {headerActions}
          </SectionActionsContainer>
        </SectionHeading>
        <SectionInputWrapper>
          {isLoading && <StyledSpinner />}
          {!isLoading && (
            <FocusGrid data-testid={`${entityKind}-focus-tiles`}>
              {tiles.map((tile, index) => {
                const propertyName = formatPropertyName(tile);
                // Absence outranks the live column list: picking a shapefile
                // in group-by materializes its columns into the category list
                // and a project switch does not evict them, so a stale entry
                // could otherwise render as a healthy picker row for a field
                // this project will never use.
                const column = isAbsentShapefileField(propertyName)
                  ? null
                  : columnsToUse.find((c) => propertyName === c.property) ??
                    resolveShapefileColumn(propertyName);

                // See DefaultMapTooltipFieldSetting: rendering nothing for a
                // dead entry removed the only way to delete it, stranding it in
                // the saved list (EVA-126). Removal ignores the minimum-count
                // gate - a broken tile shouldn't be held onto to meet a minimum.
                // A shapefile deleted from this project reads "Unavailable
                // field"; one that simply belongs to another project keeps its
                // field name and says so. A tile the tree cannot judge stays
                // hidden as before.
                if (!column) {
                  if (!isAbsentShapefileField(propertyName)) return;

                  const isDeletedHere = isDeletedShapefileField(propertyName);
                  return (
                    <UnavailableTile
                      key={tile}
                      data-testid={`${entityKind}-default-focus-tiles-unavailable-${index}`}>
                      <FocusTileTitle>
                        {isDeletedHere
                          ? "Unavailable field"
                          : shapefileColumnFromKey(propertyName)?.title ??
                            "Unavailable field"}
                      </FocusTileTitle>
                      <Tooltip
                        title={
                          isDeletedHere
                            ? "This field's shapefile is no longer in this project. Remove the field, or re-link the shapefile to restore it."
                            : "This field's shapefile belongs to another project, so it is not used in this project. Remove the field, or manage it in its own project."
                        }>
                        <StyledDeleteButton
                          type="text"
                          data-testid={`${entityKind}-default-focus-tiles-delete-${index}`}
                          icon={<Icon path={mdiDelete} size={1.3} />}
                          onClick={(e) => handleRemoveFocusTile(e, index)}
                        />
                      </Tooltip>
                    </UnavailableTile>
                  );
                }

                const containsFcst = tile.includes(".fcst");
                const {
                  performanceDateRange: tileDateRange,
                  pdenSource: tilePdenSource
                } = parseTileQualifications(tile);
                // Nothing here names a forecast source, so that slot is left empty rather
                // than filled with the entity kind, which belongs in the one after it.
                const label = getColumnLabelWithContext(
                  column,
                  !!globalSettings.normalizeBy,
                  globalSettings.normalizeBy,
                  containsFcst,
                  undefined,
                  entityKind,
                  tileDateRange
                );
                // The picker seeds its date inputs from the value it is given. Without the
                // range the tile was stored with it opens empty, and re-picking the same
                // field writes the tile back without its range.
                const pickerValue = tileDateRange
                  ? { ...column, performanceDateRange: tileDateRange }
                  : column;
                return (
                  <GroupBy
                    key={tile}
                    excludedCategoryFilters={excludedCategoryFilters}
                    value={pickerValue}
                    entityKind={entityKind}
                    pinnedPdenSource={tilePdenSource}
                    onChange={(
                      groupBy,
                      isForecastToggleOn,
                      selectedForecastFolder,
                      selectedForecastFolderName,
                      selectedPdenSource,
                      performanceDateRange
                    ) => {
                      // The picker reports whatever dates its inputs hold, even for a field
                      // that has no use for them. Only a Date Range field is stored with one:
                      // a field such as Operator would keep a suffix it never reads, on a tile
                      // that looks no different, so the user could not see or undo it.
                      const storedDateRange =
                        groupBy.subgroup === "Date Range"
                          ? performanceDateRange
                          : undefined;
                      // Same for the production source, which the picker also always reports.
                      // It only offers the control for a well's performance and pad-metrics
                      // fields, and PdenSourceSelect only reads a stored source back for those
                      // — so anything else would keep a source it never uses, and would stop
                      // matching its own focus as soon as the global source changed.
                      const property = groupBy.property.toLowerCase();
                      const offersPdenSource =
                        entityKind === EntityKind.Well &&
                        (property.includes("performance") ||
                          property.startsWith("turingpadmetrics"));
                      // Even then, a source is only kept when it is not the one the tile would
                      // answer from anyway: the picker's own first option, which follows the
                      // organization's production source setting. Keeping every source the
                      // picker reports would pin every tile saved here to whatever was
                      // selected at the time, so a later change of that setting would leave
                      // the tile unlit and clicking it would drag the focus back.
                      //
                      // The comparison is against that source and not against the one the
                      // picker opened on. A pinned tile re-opened and set back to it is meant
                      // to lose its pin; measuring the change from the opening source instead
                      // would keep the pin and give the user no way to clear it.
                      const unpinnedPdenSource = toPdenEnum(globalSettings.source);
                      const storedPdenSource =
                        offersPdenSource && selectedPdenSource !== unpinnedPdenSource
                          ? selectedPdenSource
                          : undefined;
                      handleTileChange(
                        index,
                        qualifyTileProperty(
                          groupBy.property,
                          storedDateRange,
                          storedPdenSource
                        )
                      );
                    }}>
                    <StyledButton
                      data-testid={`${entityKind}-default-focus-tiles-button-${index}`}>
                      <FocusTileTitle>
                        {getFlatDisplayTitle(column.property, label, column.title)}
                      </FocusTileTitle>
                      {resolvedTiles.length > 2 && (
                        <Tooltip title="Remove focus tile">
                          <StyledDeleteButton
                            type="text"
                            data-testid={`${entityKind}-default-focus-tiles-delete-${index}`}
                            icon={<Icon path={mdiDelete} size={1.3} />}
                            onClick={(e) => handleRemoveFocusTile(e, index)}
                          />
                        </Tooltip>
                      )}
                    </StyledButton>
                  </GroupBy>
                );
              })}
            </FocusGrid>
          )}
        </SectionInputWrapper>

        {error && (
          <ErrorContainer>
            <Icon path={mdiAlert} size={1} /> {error}
          </ErrorContainer>
        )}
      </div>
    </GeomBinProvider>
  );
};

export default FocusTilePicker;

const FocusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(49%, 1fr));
  gap: 5px;
`;

const StyledDeleteButton = styled(Button)`
  position: absolute;
  right: 0;
  top: 2px;
  padding: 0;
  color: #b5b5b5;
  opacity: 0;

  &:hover {
    color: var(--color-danger);
  }
`;

const StyledButton = styled(Button)`
  position: relative;
  width: 100%;
  /* Without this a tile sizes to its whole label, so a long one pushes the row
     past the card and takes the delete button out of reach. */
  min-width: 0;
  background-color: rgba(var(--color-text-rgb), 0.05);
  color: var(--color-text);
  padding: 4px 24px;

  &:hover {
    background-color: var(--color-text);
    color: #fff;

    ${StyledDeleteButton} {
      opacity: 1;
    }
  }
`;
const FocusTileTitle = styled.div`
  text-align: center;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

// A dead tile, kept on screen only so it can be removed. Muted, not a picker
// (there is nothing behind it to open one on), and its delete button stays
// visible rather than waiting for a hover the tile's appearance doesn't invite.
const UnavailableTile = styled(StyledButton)`
  color: rgba(var(--color-text-rgb), 0.5);
  font-style: italic;
  cursor: default;

  &:hover {
    background-color: rgba(var(--color-text-rgb), 0.05);
    color: rgba(var(--color-text-rgb), 0.5);
  }

  ${StyledDeleteButton} {
    opacity: 1;
  }
`;
